<template>
  <div class="ur-landowner">
    <!-- 工具列 -->
    <div class="ur-lo-toolbar">
      <div class="ur-lo-toolbar-left"></div>
      <div class="ur-lo-toolbar-right">
        <button class="ur-lo-btn" @click="triggerImport">
          {{ $t('URBANRENEWAL_ADMIN.LANDOWNER.IMPORT') }}
        </button>
        <button class="ur-lo-btn" @click="exportData">
          {{ $t('URBANRENEWAL_ADMIN.LANDOWNER.EXPORT') }}
        </button>
        <button
          v-if="selected.length > 0"
          class="ur-lo-btn ur-lo-btn-danger"
          @click="openDelete"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="3 6 5 6 21 6"></polyline>
            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
          </svg>
          {{ $t('URBANRENEWAL_ADMIN.LANDOWNER.DELETE_N', { count: selected.length }) }}
        </button>
      </div>
      <!-- 隱藏 file input -->
      <input
        type="file"
        accept=".xlsx"
        :key="fileInputKey"
        style="display:none"
        ref="fileInput"
        @change="onFileChange"
      />
    </div>

    <!-- 錯誤提示條 -->
    <div v-if="errorMsg" class="ur-lo-error">
      <span>{{ errorMsg }}</span>
      <button class="ur-lo-error-close" @click="errorMsg = ''">&#10007;</button>
    </div>

    <!-- 讀取中 -->
    <div v-if="loading" class="ur-lo-loading">
      {{ $t('URBANRENEWAL_ADMIN.LANDOWNER.LOADING') || '載入中...' }}
    </div>

    <!-- 表格 -->
    <div v-else class="ur-table-wrap">
      <table class="ur-table">
        <thead>
          <tr>
            <th class="ur-th-check">
              <input
                type="checkbox"
                :checked="allChecked"
                :indeterminate.prop="selected.length > 0 && !allChecked"
                @change="toggleAll"
              />
            </th>
            <th>{{ $t('URBANRENEWAL_ADMIN.LANDOWNER.PRIORITY_ZONE') }}</th>
            <th>{{ $t('URBANRENEWAL_ADMIN.LANDOWNER.NAME') }}</th>
            <th>{{ $t('URBANRENEWAL_ADMIN.LANDOWNER.ACCOUNT') }}</th>
            <th class="ur-th-pwd">{{ $t('URBANRENEWAL_ADMIN.LANDOWNER.PASSWORD') }}</th>
            <th>{{ $t('URBANRENEWAL_ADMIN.LANDOWNER.ADDRESS') }}</th>
            <th>{{ $t('URBANRENEWAL_ADMIN.LANDOWNER.LAND_SHARE') }}</th>
            <th>{{ $t('URBANRENEWAL_ADMIN.LANDOWNER.PARKING_ALLOCATION') }}</th>
            <th class="ur-th-num">{{ $t('URBANRENEWAL_ADMIN.LANDOWNER.PARKING_VALUE') }}</th>
            <th class="ur-th-num">{{ $t('URBANRENEWAL_ADMIN.LANDOWNER.PRE_RATIO') }}</th>
            <th class="ur-th-num">{{ $t('URBANRENEWAL_ADMIN.LANDOWNER.PRE_VALUE') }}</th>
            <th class="ur-th-num">{{ $t('URBANRENEWAL_ADMIN.LANDOWNER.ALLOCATED_VALUE') }}</th>
            <th class="ur-th-num">{{ $t('URBANRENEWAL_ADMIN.LANDOWNER.SELECTABLE_VALUE') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in pagedRows"
            :key="row.id"
            :class="{ 'ur-tr-selected': selected.includes(row.id) }"
          >
            <!-- Checkbox -->
            <td class="ur-td-check">
              <input
                type="checkbox"
                :checked="selected.includes(row.id)"
                @change="toggleRow(row.id)"
              />
            </td>
            <!-- 優先選區 -->
            <td>
              <UrbanRenewalInlineEdit
                :value="row.priority_zone"
                type="text"
                @save="onCellSave(row, 'priority_zone', $event)"
              />
            </td>
            <!-- 地主姓名 -->
            <td>
              <UrbanRenewalInlineEdit
                :value="row.display_name"
                type="text"
                @save="onCellSave(row, 'display_name', $event)"
              />
            </td>
            <!-- 帳號（可編輯；帳號為共用登入，改動影響共用此帳號的所有地主）-->
            <td>
              <UrbanRenewalInlineEdit
                :value="row.username"
                type="text"
                @save="onCellSave(row, 'username', $event)"
              />
            </td>
            <!-- 密碼：點內容即 inline 編輯；眼睛只切換明碼/遮碼 -->
            <td class="ur-td-pwd">
              <div class="ur-pwd-cell">
                <UrbanRenewalInlineEdit
                  class="ur-pwd-inline"
                  :value="row.plain_password"
                  type="text"
                  :display="pwdVisible[row.id] ? row.plain_password : '••••••••'"
                  @save="onPasswordSave(row, $event)"
                />
                <button
                  class="ur-pwd-eye-btn"
                  @click="togglePwd(row.id)"
                  :title="pwdVisible[row.id] ? '隱藏密碼' : '顯示密碼'"
                >
                  <!-- Eye open -->
                  <svg v-if="pwdVisible[row.id]" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                  <!-- Eye closed -->
                  <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                    <line x1="1" y1="1" x2="23" y2="23"></line>
                  </svg>
                </button>
              </div>
            </td>
            <!-- 建物門牌 -->
            <td>
              <UrbanRenewalInlineEdit
                :value="row.address"
                type="text"
                @save="onCellSave(row, 'address', $event)"
              />
            </td>
            <!-- 土地持分 -->
            <td>
              <UrbanRenewalInlineEdit
                :value="row.land_share"
                type="text"
                @save="onCellSave(row, 'land_share', $event)"
              />
            </td>
            <!-- 車位分配 -->
            <td>
              <UrbanRenewalInlineEdit
                :value="row.parking_allocation"
                type="float"
                @save="onCellSave(row, 'parking_allocation', $event)"
              />
            </td>
            <!-- 車位價值 -->
            <td class="ur-td-num">
              <UrbanRenewalInlineEdit
                :value="row.parking_value"
                type="int"
                :display="fmt(row.parking_value)"
                @save="onCellSave(row, 'parking_value', $event)"
              />
            </td>
            <!-- 更新前權值(%) -->
            <td class="ur-td-num">
              <UrbanRenewalInlineEdit
                :value="row.pre_update_ratio"
                type="float"
                @save="onCellSave(row, 'pre_update_ratio', $event)"
              />
            </td>
            <!-- 更新前價值 -->
            <td class="ur-td-num">
              <UrbanRenewalInlineEdit
                :value="row.pre_update_value"
                type="int"
                :display="fmt(row.pre_update_value)"
                @save="onCellSave(row, 'pre_update_value', $event)"
              />
            </td>
            <!-- 應分配價值 -->
            <td class="ur-td-num">
              <UrbanRenewalInlineEdit
                :value="row.allocated_value"
                type="int"
                :display="fmt(row.allocated_value)"
                @save="onCellSave(row, 'allocated_value', $event)"
              />
            </td>
            <!-- 可選配價值 -->
            <td class="ur-td-num">
              <UrbanRenewalInlineEdit
                :value="row.selectable_value"
                type="int"
                :display="fmt(row.selectable_value)"
                @save="onCellSave(row, 'selectable_value', $event)"
              />
            </td>
          </tr>
          <tr v-if="pagedRows.length === 0 && !loading">
            <td colspan="13" class="ur-td-empty">
              {{ projectId ? $t('URBANRENEWAL_ADMIN.LANDOWNER.NO_DATA') || '無資料' : $t('URBANRENEWAL_ADMIN.LANDOWNER.SELECT_PROJECT') || '請先選擇建案' }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- 分頁列（共用元件，設計稿樣式）-->
    <UrbanRenewalPagination
      :total="rows.length"
      :page.sync="page"
      :rpp.sync="rpp"
    />

    <!-- 刪除確認 dialog -->
    <v-dialog v-model="deleteDialog.visible" max-width="420">
      <v-card class="ur-dialog-card">
        <div class="ur-dialog-body">
          <p class="ur-dialog-msg">
            {{ $t('URBANRENEWAL_ADMIN.LANDOWNER.DELETE_CONFIRM', { count: selected.length }) }}
          </p>
        </div>
        <div class="ur-dialog-actions">
          <button class="ur-dialog-cancel-btn" @click="deleteDialog.visible = false">
            {{ $t('URBANRENEWAL_ADMIN.LANDOWNER.CANCEL') }}
          </button>
          <button class="ur-dialog-confirm-btn" @click="confirmDelete">
            {{ $t('URBANRENEWAL_ADMIN.LANDOWNER.CONFIRM') }}
          </button>
        </div>
      </v-card>
    </v-dialog>

    <!-- 匯入結果 dialog（成功 / 部分 / 錯誤 統一呈現） -->
    <v-dialog v-model="importResultDialog.visible" max-width="600">
      <v-card class="ur-dialog-card">
        <div class="ur-dialog-body">
          <p class="ur-dialog-title">{{ $t('URBANRENEWAL_ADMIN.LANDOWNER.IMPORT_ERROR_TITLE') }}</p>

          <!-- 結構/檔案錯誤（400）：整份未匯入 -->
          <p v-if="importResultDialog.errorDetail" class="ur-dialog-msg ur-import-error-detail">
            {{ importResultDialog.errorDetail }}
          </p>

          <!-- 有結果（200） -->
          <template v-else>
            <!-- 全部成功、無跳過 -->
            <p v-if="importResultDialog.skipped.length === 0" class="ur-dialog-msg ur-import-success">
              {{ $t('URBANRENEWAL_ADMIN.LANDOWNER.IMPORT_SUCCESS', {
                created: importResultDialog.imported ? importResultDialog.imported.created || 0 : 0,
                updated: importResultDialog.imported ? importResultDialog.imported.updated || 0 : 0
              }) }}
            </p>
            <!-- 部分匯入：摘要 + 錯誤表 -->
            <template v-else>
              <p class="ur-dialog-msg">
                {{ $t('URBANRENEWAL_ADMIN.LANDOWNER.IMPORT_SUMMARY', {
                  created: importResultDialog.imported ? importResultDialog.imported.created || 0 : 0,
                  updated: importResultDialog.imported ? importResultDialog.imported.updated || 0 : 0
                }) }}
              </p>
              <div class="ur-import-error-table-wrap">
                <table class="ur-import-error-table">
                  <thead>
                    <tr>
                      <th>{{ $t('URBANRENEWAL_ADMIN.LANDOWNER.COL_ROW') }}</th>
                      <th>{{ $t('URBANRENEWAL_ADMIN.LANDOWNER.COL_REASON') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(e, idx) in importResultDialog.skipped" :key="idx">
                      <td>{{ e.row }}</td>
                      <td>{{ Array.isArray(e.reasons) ? e.reasons.join('、') : e.reasons }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </template>
          </template>
        </div>
        <div class="ur-dialog-actions">
          <button class="ur-dialog-confirm-btn" @click="importResultDialog.visible = false">
            {{ $t('URBANRENEWAL_ADMIN.LANDOWNER.CLOSE') }}
          </button>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import UrbanRenewalDataService from '@/services/UrbanRenewalDataService';
import UrbanRenewalInlineEdit from '@/components/urbanrenewal/UrbanRenewalInlineEdit.vue';
import UrbanRenewalPagination from '@/components/urbanrenewal/UrbanRenewalPagination.vue';

export default {
  name: 'LandownerTab',

  components: { UrbanRenewalInlineEdit, UrbanRenewalPagination },

  props: {
    projectId: {
      type: Number,
      default: null,
    },
  },

  data() {
    return {
      rows: [],
      loading: false,
      selected: [],
      page: 1,
      rpp: 20,
      pwdVisible: {},
      deleteDialog: { visible: false },
      importResultDialog: { visible: false, imported: null, skipped: [], errorDetail: '' },
      errorMsg: '',
      fileInputKey: 0,
    };
  },

  computed: {
    pagedRows() {
      const start = (this.page - 1) * this.rpp;
      return this.rows.slice(start, start + this.rpp);
    },

    pageIds() {
      return this.pagedRows.map((r) => r.id);
    },

    allChecked() {
      if (this.pagedRows.length === 0) return false;
      return this.pageIds.every((id) => this.selected.includes(id));
    },

    totalPages() {
      return Math.ceil(this.rows.length / this.rpp) || 1;
    },

    rppModel: {
      get() { return this.rpp; },
      set(val) { this.rpp = Number(val); this.page = 1; },
    },
  },

  watch: {
    projectId: {
      immediate: true,
      handler() {
        this.loadRows();
      },
    },
    rows(v) {
      this.$emit('update:count', v.length);
    },
  },

  methods: {
    async loadRows() {
      if (!this.projectId) {
        this.rows = [];
        return;
      }
      this.loading = true;
      try {
        const res = await UrbanRenewalDataService.getLandowners(this.projectId);
        this.rows = res.data;
        this.selected = [];
        this.page = 1;
      } catch (err) {
        this.errorMsg = (err.response && err.response.data && err.response.data.detail) || '載入失敗';
      } finally {
        this.loading = false;
      }
    },

    async onCellSave(row, field, val) {
      try {
        await UrbanRenewalDataService.updateLandowner(this.projectId, row.id, { [field]: val });
        if (field === 'username') {
          // 帳號為共用登入：重載清單，讓共用同帳號的所有列一起同步為最新值（避免顯示成不同帳號）
          await this.loadRows();
        } else {
          row[field] = val;
        }
      } catch (err) {
        // 後端欄位錯誤（如帳號重複）優先顯示具體訊息
        const data = err.response && err.response.data;
        let msg = this.$t('URBANRENEWAL_ADMIN.LANDOWNER.SAVE_FAILED');
        if (data) {
          const first = data.detail || (typeof data === 'object' ? Object.values(data)[0] : null);
          if (first) msg = Array.isArray(first) ? first[0] : first;
        }
        this.errorMsg = msg;
      }
    },

    async onPasswordSave(row, val) {
      try {
        await UrbanRenewalDataService.updateLandowner(this.projectId, row.id, { password: val });
        row.plain_password = val;
      } catch (err) {
        this.errorMsg = this.$t('URBANRENEWAL_ADMIN.LANDOWNER.SAVE_FAILED');
      }
    },

    toggleRow(id) {
      const idx = this.selected.indexOf(id);
      if (idx === -1) {
        this.selected.push(id);
      } else {
        this.selected.splice(idx, 1);
      }
    },

    toggleAll() {
      if (this.allChecked) {
        this.selected = this.selected.filter((id) => !this.pageIds.includes(id));
      } else {
        this.pageIds.forEach((id) => {
          if (!this.selected.includes(id)) {
            this.selected.push(id);
          }
        });
      }
    },

    togglePwd(id) {
      this.$set(this.pwdVisible, id, !this.pwdVisible[id]);
    },

    openDelete() {
      if (this.selected.length === 0) return;
      this.deleteDialog.visible = true;
    },

    async confirmDelete() {
      try {
        await UrbanRenewalDataService.deleteLandowners(this.projectId, this.selected);
        this.deleteDialog.visible = false;
        this.selected = [];
        await this.loadRows();
      } catch (err) {
        this.errorMsg = (err.response && err.response.data && err.response.data.detail) || '刪除失敗';
        this.deleteDialog.visible = false;
      }
    },

    triggerImport() {
      this.$refs.fileInput.click();
    },

    async onFileChange(e) {
      const file = e.target.files && e.target.files[0];
      if (!file) return;
      const formData = new FormData();
      formData.append('file', file);
      try {
        const res = await UrbanRenewalDataService.importLandowners(this.projectId, formData);
        const { imported, skipped } = res.data;
        await this.loadRows();
        this.importResultDialog = {
          visible: true,
          imported,
          skipped: skipped || [],
          errorDetail: '',
        };
      } catch (err) {
        const detail =
          (err.response && err.response.data && err.response.data.detail) ||
          this.$t('URBANRENEWAL_ADMIN.LANDOWNER.IMPORT_FAILED');
        this.importResultDialog = {
          visible: true,
          imported: null,
          skipped: [],
          errorDetail: detail,
        };
      } finally {
        this.fileInputKey++;
      }
    },

    async exportData() {
      try {
        const res = await UrbanRenewalDataService.exportLandowners(this.projectId);
        const blob = new Blob([res.data], { type: res.headers['content-type'] });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        let fn = '地主資料.xlsx';
        const cd = res.headers['content-disposition'];
        if (cd) {
          const m = /filename\*?=(?:UTF-8'')?["']?([^"';]+)/i.exec(cd);
          if (m) fn = decodeURIComponent(m[1]);
        }
        a.download = fn;
        a.click();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        this.errorMsg = '匯出失敗';
      }
    },

    fmt(v) {
      if (v === null || v === undefined) return '';
      return Number(v).toLocaleString('zh-TW', { maximumFractionDigits: 0 });
    },
  },
};
</script>
